import React, { useReducer, useState, useEffect, useRef, useCallback } from "react";
import classNames from "classnames";
import { useTranslation, Trans } from "react-i18next";
import { GoogleOAuthProvider } from "@react-oauth/google";
// import { useLocation } from "react-router-dom";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

import "@/i18n";
import validations from "@/shared/validations/validations";
import { checkLogin } from "@/api/login";
import loadFacebookSDK from "./FacebookSDK";
import WrongPartner from "../WrongPartner/WrongPartner";
import SocialButton from "../SocialButton/SocialButton";
import FormContext from "../../state/FormContext";
import { reducer, initialState } from "../../state/reducer";
import { useTheme } from "@/shared/styles/useTheme";
import { Input, Button, Icon } from "@/shared/components";
import { parseLoginError, handleLogin, navigateTo, handleRegistration } from "../../utils/authUtils";
import type { RegisterFlagsT } from "@/api/login";

import Styles from "./LoginOrRegister.module.scss";
import MagicLink from "./MagicLink";
import {
  InternalDataProtectionRoute,
  InternalUserSomethingRoute,
} from "@/cms/components/ContentPages/misc/ContentPageRoutes";
import RegistrationSuccess from "./RegistrationSuccess";
import { useTracking } from "@/tracking/tracking/Tracking";
import useValidate from "@/shared/validations/useValidate";
import { Seo } from "@/cms/components/Seo/Seo";

const PasswordForgottenRoute = "/passwort-vergessen";

type Props = {
  url?: string;
  title?: {
    initial: React.ReactNode;
    login: React.ReactNode;
    register: React.ReactNode;
  };
  social?: boolean;
  flags?: RegisterFlagsT;
  stepBackFunction?: () => void;
  loginCtaID?: string;
  registerCtaID?: string;
  fbCtaId?: string;
  googleCtaId?: string;
  mode?: "Navbar" | "SOF" | "CSF";
  onLoginSuccess?: () => void;
  primaryColor?: string;
};

const LoginOrRegister = ({
  title,
  social = true,
  flags = { regOnlyNaviFlag: true },
  stepBackFunction,
  loginCtaID,
  registerCtaID,
  fbCtaId,
  googleCtaId,
  onLoginSuccess,
  mode = "Navbar",
  primaryColor,
}: Props) => {
  const { error, errorBorder, link, primaryColorBorder } = useTheme();
  const { t } = useTranslation();
  const [fbLoaded, setFbLoaded] = useState(false);
  const [showMegicLinkSuccessMsg, setShowMegicLinkSuccessMsg] = useState(false);
  const [state, dispatch] = useReducer(reducer, initialState);
  const [wrongPartner, setWrongPartner] = useState<undefined | { link: string; partnerName: string }>(undefined);
  const [runValidation, setRunValidation] = useState<boolean>(false);
  const [passwordVisible, setPasswordVisible] = useState(false);
  const emailRef = useRef<HTMLInputElement>(null);
  const passwordRef = useRef<HTMLInputElement>(null);
  const [registrationSuccess, setRegistrationSuccess] = useState(false);

  // Use the primary green color from the theme or fallback to the provided primaryColor
  const themeColor = primaryColor || "#04884B";

  // const { search } = useLocation();
  const search = window.location.search;
  const searchParams = new URLSearchParams(search);
  const linkExpired = searchParams.get("linkExpired") === "true";
  const tracking = useTracking();
  const passwordError = useValidate(
    state.values.password,
    [validations.required, validations.isPassword],
    t("auth.errors.password")
  );
  const passwordConfirmError = useValidate(
    state.values.passwordConfirm,
    [validations.required, validations.isConfirmedPassword(state.values.password), validations.isPassword],
    t("auth.errors.passwordConfirm")
  );
  const emailError = useValidate(
    state.values.email,
    [validations.required, validations.isEmail],
    t("auth.errors.email")
  );

  useEffect(() => {
    loadFacebookSDK()
      .then(() => setFbLoaded(true))
      .catch((err) => console.log("FacebookSDK failed to load: ", err));
  }, []);

  const handleEmailBlur = useCallback(() => {
    if (state.values.email && !state.errors.email) {
      const trimmedEmail = state.values.email?.trim();
      checkLogin(trimmedEmail)
        .then((res) => {
          if (typeof res === "object" && res !== null && "status" in res) {
            const { status, data } = res;
            if (status === 404) {
              dispatch({
                type: "SET_MODE",
                payload: "register",
              });
            } else if (status === 409) {
              if (data.message === "user.email.already_exists") {
                dispatch({
                  type: "SET_IS_REGISTERED_USER",
                  payload: true,
                });
                dispatch({
                  type: "SET_MODE",
                  payload: "login",
                });
              } else if (data.message === "user.email.already_exists_as_change_email") {
                dispatch({
                  type: "CHANGE_ERRORS",
                  payload: { email: t("auth.errors.already_exists_as_change_email") },
                });

                setRunValidation(true);
              }
            }
          } else if (typeof res === "number") {
            // handle the case where res is a number, if needed
          }
        })
        .catch((err) => {
          throw err;
        });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, state.values.email, state.errors.email]);

  const handleSubmit = async (e: React.SyntheticEvent) => {
    e.preventDefault();
    setRunValidation(true);

    if (!state.errors.email && !state.errors.password) {
      try {
        if (state.mode === "register") {
          if (state.values.password !== state.values.passwordConfirm) {
            dispatch({
              type: "CHANGE_ERRORS",
              payload: { passwordConfirm: t("auth.errors.passwordMismatch") },
            });
            return;
          }

          await handleRegistration(
            state.values.email,
            state.values.password,
            window.location.pathname,
            flags,
            tracking
          );
          setRegistrationSuccess(true);
        } else {
          const response = await handleLogin(state.values.email, state.values.password);
          if (response?.error) {
            const error = parseLoginError(response.error, response.error_description, t, setWrongPartner);
            dispatch({
              type: "CHANGE_ERRORS",
              payload: { login: error },
            });
          } else {
            onLoginSuccess?.();
          }
        }
      } catch (err) {
        if (err instanceof Error) {
          toast.error(err.message);
        }
        throw err;
      }
    }
  };

  const togglePasswordDisplay = () => setPasswordVisible(!passwordVisible);

  const handleBackToLogin = () => {
    if (stepBackFunction) {
      stepBackFunction();
    }
  };

  if (registrationSuccess) {
    return (
      <div className={Styles.wrapper}>
        <RegistrationSuccess mode={mode} />
      </div>
    );
  }

  if (wrongPartner) {
    return <WrongPartner name={wrongPartner.partnerName} url={wrongPartner.link} />;
  }

  return (
    <>
      <Seo title={t("auth.seo.title")} description={t("auth.seo.description")} />
      <ToastContainer />
      <GoogleOAuthProvider clientId={process.env.GOOGLE_CLIENT_ID ?? ""}>
        <FormContext.Provider value={{ state, dispatch }}>
          <div className={Styles.wrapper} style={{ "--primaryColor": themeColor } as React.CSSProperties}>
            {!showMegicLinkSuccessMsg && (
              <div className={classNames(Styles.form, primaryColorBorder)}>
                <form onSubmit={handleSubmit}>
                  {title ? (
                    title[state.mode]
                  ) : (
                    <>
                      <h1 className={Styles.loginTitle}>
                        {linkExpired ? t("magicLink.expiredTitle") : t("auth.loginOrRegister.title")}
                      </h1>
                      <div className={Styles.loginSubtitle}>
                        {linkExpired ? t("magicLink.expiredMessage") : t("auth.loginOrRegister.subtitle")}
                      </div>
                    </>
                  )}
                  {/* Email Field */}
                  <div className={Styles.fieldWrapper}>
                    <label htmlFor="email">{t("auth.enterEmail")}</label>
                    <Input
                      reference={emailRef}
                      fullWidth
                      id="email"
                      name="email"
                      value={state.values.email}
                      placeholder={t("auth.emailAddress") || undefined}
                      validate={state.values.email ? [validations.isEmail] : undefined}
                      showValidation={runValidation}
                      onBlur={handleEmailBlur}
                      onChange={(event) => {
                        const trimmedEmail = event.target.value?.trim();
                        dispatch({
                          type: "CHANGE_VALUES",
                          payload: { email: trimmedEmail },
                        });
                        dispatch({
                          type: "SET_IS_REGISTERED_USER",
                          payload: false,
                        });
                      }}
                      onValidate={(error: string | undefined) =>
                        dispatch({
                          type: "CHANGE_ERRORS",
                          payload: { email: error },
                        })
                      }
                      errorMessage={state.errors.email}
                    />
                  </div>

                  {/* Password Field */}
                  <div className={Styles.fieldWrapper}>
                    <label htmlFor="password">
                      {state.mode === "register" ? t("auth.yourPassword") : t("auth.password")}
                    </label>
                    <div className={Styles.passwordFieldContainer}>
                      <Input
                        reference={passwordRef}
                        fullWidth
                        type={passwordVisible ? "text" : "password"}
                        id="password"
                        name="password"
                        value={state.values.password}
                        placeholder={state.mode === "register" ? t("auth.yourPasswordPlaceholder") : t("auth.password")}
                        showValidation={runValidation}
                        validate={
                          state.mode === "register" ? [validations.required, validations.isPassword] : undefined
                        }
                        onChange={(event) => {
                          dispatch({
                            type: "CHANGE_ERRORS",
                            payload: { password: undefined },
                          });
                          dispatch({
                            type: "CHANGE_VALUES",
                            payload: { password: event.target.value },
                          });
                        }}
                        onValidate={
                          state.mode === "register"
                            ? (error: string | undefined) =>
                                dispatch({
                                  type: "CHANGE_ERRORS",
                                  payload: { password: error },
                                })
                            : undefined
                        }
                      />
                      <Icon
                        className={Styles.togglePasswordIcon}
                        name={passwordVisible ? "eyeCross" : "eye"}
                        onClick={togglePasswordDisplay}
                      />
                    </div>
                  </div>

                  {/* Confirm Password Field (Register Mode) */}
                  {state.mode === "register" && (
                    <div className={Styles.fieldWrapper}>
                      <div className={Styles.passwordFieldContainer}>
                        <Input
                          fullWidth
                          type={passwordVisible ? "text" : "password"}
                          id="passwordConfirm"
                          name="passwordConfirm"
                          value={state.values.passwordConfirm}
                          placeholder={
                            state.mode === "register" ? t("auth.yourRepeatedPasswordPlaceholder") : t("auth.password")
                          }
                          showValidation={runValidation}
                          validate={[validations.required, validations.isConfirmedPassword(state.values.password)]}
                          onChange={(event) => {
                            dispatch({
                              type: "CHANGE_ERRORS",
                              payload: { passwordConfirm: undefined },
                            });
                            dispatch({
                              type: "CHANGE_VALUES",
                              payload: { passwordConfirm: event.target.value },
                            });
                          }}
                          onValidate={(error: string | undefined) =>
                            dispatch({
                              type: "CHANGE_ERRORS",
                              payload: { passwordConfirm: error },
                            })
                          }
                        />
                        <Icon
                          className={Styles.togglePasswordIcon}
                          name={passwordVisible ? "eyeCross" : "eye"}
                          onClick={togglePasswordDisplay}
                        />
                      </div>
                    </div>
                  )}

                  {/* Error Display */}
                  {state.errors?.login && (
                    <div className={classNames(Styles.errorContainer, error, errorBorder)}>
                      <Icon name={"errorOutline"} className={Styles.errorIcon} />
                      <span className={Styles.creationError}>{state.errors?.login}</span>
                    </div>
                  )}

                  {/* Forgot Password Link */}
                  {state.mode === "login" && (
                    <div className={Styles.forgotPassword}>
                      <span
                        className={link}
                        onClick={() => navigateTo(PasswordForgottenRoute, { email: state.values.email })}
                      >
                        {t("auth.forgotPassword")}
                      </span>
                    </div>
                  )}

                  {/* Buttons */}
                  <div className={Styles.buttonsWrapper}>
                    {stepBackFunction && (
                      <Button fullWidth secondary onClick={handleBackToLogin} className={Styles.backButton}>
                        <>
                          <Icon name="backArrow" />
                          <span>{t("auth.back")}</span>
                        </>
                      </Button>
                    )}
                    <Button
                      fullWidth
                      submit
                      disabled={
                        !state.values.email ||
                        !state.values.password ||
                        !!state.errors.email ||
                        !!emailError ||
                        (state.mode === "register" &&
                          (!state.values.password ||
                            !state.values.passwordConfirm ||
                            !!state.errors.password ||
                            !!state.errors.passwordConfirm ||
                            state.values.password !== state.values.passwordConfirm ||
                            !!passwordError ||
                            !!passwordConfirmError))
                      }
                      id={state.mode === "register" ? registerCtaID : loginCtaID}
                    >
                      {state.mode === "register" ? t("auth.registration.submit") : t("auth.title.login")}
                    </Button>
                  </div>
                </form>
              </div>
            )}
            {state.isRegisteredUser &&
              state.values.email &&
              !state.errors.email &&
              state.mode === "login" &&
              mode === "Navbar" && (
                <MagicLink
                  toggleMessage={setShowMegicLinkSuccessMsg}
                  messageCase={showMegicLinkSuccessMsg}
                  linkExpiredCase={!!linkExpired}
                  mode={mode}
                />
              )}

            {!wrongPartner && !showMegicLinkSuccessMsg && (
              <>
                {social && (
                  <>
                    <div className={Styles.separator}>
                      <span>{t("auth.socialTitle")}</span>
                    </div>
                    <div className={Styles.socialBtns}>
                      <SocialButton type="google" flags={flags} ctaId={googleCtaId} onSuccess={onLoginSuccess} />
                      {fbLoaded && (
                        <SocialButton type="facebook" flags={flags} ctaId={fbCtaId} onSuccess={onLoginSuccess} />
                      )}
                    </div>
                    <div className={Styles.terms}>
                      <Trans
                        i18nKey="auth.agreeToS"
                        components={{
                          1: <a href={InternalUserSomethingRoute} className={Styles.link} />,
                          2: <a href={InternalDataProtectionRoute} className={Styles.link} />,
                        }}
                        values={{ submitButtonText: t("auth.registration.submit") }}
                      />
                    </div>
                  </>
                )}
              </>
            )}
          </div>
        </FormContext.Provider>
      </GoogleOAuthProvider>
    </>
  );
};

export default LoginOrRegister;
